import React from 'react';
/**
 * `PageInfo` is the route-aware desktop sidebar for dealer and customer
 * journeys.
 *
 * It composes different panels based on `currentPath`, including:
 * - vehicle/product/create-plan page info (reasons + just-so-you-know)
 * - a plan summary for later steps
 * - useful documents (CMS docs and/or product docs)
 * - a confirmation background image panel
 *
 * It also adapts behaviour for call-centre customer users (CCW).
 */
import { Journey, PageAbsolutePath } from '../../consts/Common';
import PageInfoPlanSummary from './PageInfoPlanSummary';
import PageInfoProduct from './PageInfoProduct';
import './PageInfo.scss';
import PageInfoCreatePlan from './PageInfoCreatePlan';
import PageInfoVehicle from './PageInfoVehicle';
import UsefulDocuments from './UsefulDocuments';
import { DocumentView } from '../common/Constants';
import { DocumentTreeItem, ProductType } from '../common/CommonTypes';
import { useAppSelector } from '../../app-state/Hooks';

type PageInfoProps = {
  documents: DocumentTreeItem[];
  expandedDocIds: string[];
  products: ProductType[];
  currentPath: string;
  setExpandedDocIds: (docIds: string[]) => void;
};

const PageInfo = ({
  documents,
  expandedDocIds,
  products,
  currentPath,
  setExpandedDocIds
}: PageInfoProps): JSX.Element => {
  const user = useAppSelector((s) => s.user);
  const cms = useAppSelector((s) => s.content);
  const appConfig = useAppSelector((s) => s.appConfig);

  const isCCWUser: boolean =
    appConfig.journeyType === Journey.Customer && !!user?.userId;

  const pageInfoSummaryClass = isCCWUser
    ? 'desktop-content page-info-container sticky-content'
    : 'desktop-content sticky-content';

  switch (currentPath) {
    case PageAbsolutePath.VehicleDetails:
    case PageAbsolutePath.Customer.VehicleDetails:
      return (
        <div className='desktop-content page-info-container sticky-content'>
          <PageInfoVehicle showReasonsToProtect={!isCCWUser} />
          {!isCCWUser && (
            <UsefulDocuments
              documents={[]}
              cmsDocuments={cms.usefulDocuments}
              view={DocumentView.Desktop}
              expandedDocIds={expandedDocIds}
              setExpandedDocIds={setExpandedDocIds}
              showDivider={!isCCWUser}
            />
          )}
        </div>
      );
    case PageAbsolutePath.Products:
    case PageAbsolutePath.Customer.Products:
      return (
        <div className='desktop-content page-info-container sticky-content'>
          <PageInfoProduct showReasonsToProtect={!isCCWUser} />
          <UsefulDocuments
            documents={documents}
            cmsDocuments={cms.usefulDocuments}
            view={DocumentView.Desktop}
            expandedDocIds={expandedDocIds}
            setExpandedDocIds={setExpandedDocIds}
            showDivider={!isCCWUser}
          />
        </div>
      );
    case PageAbsolutePath.CreatePlan:
    case PageAbsolutePath.Customer.CreatePlan:
      return (
        <div className='desktop-content page-info-container sticky-content'>
          <PageInfoCreatePlan showReasonsToProtect={!isCCWUser} />
          <UsefulDocuments
            documents={documents}
            cmsDocuments={cms.usefulDocuments}
            view={DocumentView.Desktop}
            expandedDocIds={expandedDocIds}
            setExpandedDocIds={setExpandedDocIds}
            showDivider={true}
          />
        </div>
      );
    case PageAbsolutePath.Customer.PersonalDetails:
    case PageAbsolutePath.PersonalDetails:
    case PageAbsolutePath.Summary:
    case PageAbsolutePath.Customer.Summary:
    case PageAbsolutePath.DirectDebit:
    case PageAbsolutePath.Customer.DirectDebit:
    case PageAbsolutePath.Customer.SinglePayment:
    case PageAbsolutePath.SinglePayment:
      return (
        <div className={pageInfoSummaryClass}>
          {!isCCWUser && cms.common?.hideCustomerPlan !== true && (
            <PageInfoPlanSummary
              products={products}
              culture={appConfig.cultureCode}
            />
          )}
          <div className='m2-left'>
            <UsefulDocuments
              documents={documents}
              cmsDocuments={cms.usefulDocuments}
              view={DocumentView.Desktop}
              expandedDocIds={expandedDocIds}
              setExpandedDocIds={setExpandedDocIds}
              showDivider={false}
            />
          </div>
        </div>
      );
    case PageAbsolutePath.Confirmation:
    case PageAbsolutePath.Customer.Confirmation: {
      return (
        <div className='desktop-content-full'>
          <img
            className='landing-image'
            src={cms.confirmationPage.backgroundImageUrl}
            alt={cms.confirmationPage.backgroundImageAltText}
          />
        </div>
      );
    }
    default:
      return <div className='desktop-content sticky-content'></div>;
  }
};

export default PageInfo;
